'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import { orderServiceMika } from '@/_service/orders.service';
import { getStatusName } from '@/_utils/getStatus';
import { getBrandSlug } from '@/_lib/brand-href';
import { Link } from '@/i18n/navigation';
import { Box, Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { EmblaOptionsType } from 'embla-carousel';
import ClassNames from 'embla-carousel-class-names';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useTranslations } from 'next-intl';
import React from 'react';
import { useSelector } from 'react-redux';
import { buildImageSrc } from '../CategoriesList/utils';
import { IRedux } from '@/_store/types';
import { BRAND_LOGO } from '@/_utils/Constants';

const OPTIONS: EmblaOptionsType = {
  align: 'start',
  slidesToScroll: 1,
};

const YourOrders = () => {
  const t = useTranslations();
  const user = useSelector((state: IRedux) => state.auth.user);

  const isDesktop = useIsDesktop();

  const { data } = useQuery({
    queryKey: ['customer-orders'],
    queryFn: () =>
      orderServiceMika.getList(user?.id || '', {
        page: 1,
        limit: 10,
        status_ids:
          '6ba783a3-1c2e-479c-9626-25526b3d9d36,e665273d-5415-4243-a329-aee410e39465',
      }),
    enabled: Boolean(user),
  });

  const [emblaRef] = useEmblaCarousel(OPTIONS, [
    ClassNames(),
    WheelGesturesPlugin(),
  ]);

  if (!data?.orders || !data?.orders?.length) {
    return null;
  }

  return (
    <Box py={{ base: 5, lg: 8 }} display={user ? 'block' : 'none'}>
      <Text
        fontSize={{ base: 'xl', lg: '2xl' }}
        px={{ base: 4, lg: 14 }}
        fontWeight={700}
        color='#000'
        mb={{ base: 4, lg: 5 }}
      >
        {t('you_ordered')}
      </Text>

      <Box className='embla embla--orders'>
        <Box
          className='embla__viewport'
          px={{ base: 4, lg: 14 }}
          ref={emblaRef}
        >
          <Flex className='embla__container'>
            {data?.orders?.map((order) => (
              <Box key={order.id} className='embla__slide'>
                <Link
                  href={
                    isDesktop
                      ? `/orders?id=${order.id}&shipper_id=${getBrandSlug(order.shipper.id)}`
                      : `/orders/${order.id}?shipper_id=${getBrandSlug(order.shipper.id)}`
                  }
                >
                  <VStack
                    alignItems='flex-start'
                    bg='gray.100'
                    p={{ base: 4, lg: 5 }}
                    rounded='2xl'
                    gap='4'
                  >
                    <Flex w='full' justifyContent='space-between'>
                      <VStack alignItems='flex-start' gap='1'>
                        <Text
                          as='p'
                          fontSize={{ base: 'lg', lg: 'xl' }}
                          fontWeight='bold'
                          color='dark.cardTitle'
                          lineHeight='120%'
                        >
                          {order?.shipper?.name}
                        </Text>
                        <Text
                          fontSize='sm'
                          fontWeight='medium'
                          color='gray.700'
                        >
                          {order?.created_at
                            ? format(order?.created_at, 'dd MMM HH:mm')
                            : ''}
                        </Text>
                      </VStack>

                      <VStack gap='0' alignItems='flex-end'>
                        <Text
                          fontSize={{ base: 'md', lg: 'lg' }}
                          fontWeight='bold'
                          color='dark.cardTitle'
                        >
                          {Intl.NumberFormat('ru-RU').format(
                            order.order_amount || 0,
                          )}{' '}
                          UZS
                        </Text>
                        <Text
                          fontSize='sm'
                          color='green.700'
                          fontWeight='medium'
                        >
                          {t(`order_list.${getStatusName(order.status_id)}`)}
                        </Text>
                      </VStack>
                    </Flex>

                    <HStack>
                      {order?.products?.slice(0, 6).map((product) => {
                        const image = buildImageSrc(product?.image);

                        return (
                          <Box
                            w={{ base: '40px', lg: '48px' }}
                            h={{ base: '40px', lg: '48px' }}
                            bg='gray.200'
                            rounded='lg'
                            overflow='hidden'
                            key={product.id}
                          >
                            {image ? (
                              <Image
                                src={image}
                                alt='product'
                                objectFit='cover'
                                height='full'
                                width='full'
                              />
                            ) : (
                              <Image
                                src={BRAND_LOGO[order?.shipper_id]}
                                alt='product'
                              />
                            )}
                          </Box>
                        );
                      })}
                    </HStack>
                  </VStack>
                </Link>
              </Box>
            ))}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default YourOrders;
